'use client'

import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import axios from 'axios'
import { useMarleeUIAction } from '@/app/components/marlee/useMarleeUIAction'
import { useToast } from '@/app/context/toast-provider'
import { useSSEV2 } from '@/app/hooks/useSSEV2'
import { requestHandler } from '@/app/utils/requestHandler'
import { InternalEventType } from '@/lib/sse/InternalEvent.constant'

export type UseInterviewDataOptions = {
    jobApplicationId: number
    /** Called whenever the interview changes (a fetch, or a local `setInterview`). */
    onInterviewDataChange?: (interview: any) => void
    /** Refreshes the job application list after changes that show up there (link sent, round booked, …). */
    getCandidateList?: () => void | Promise<void>
    /** After a questions-generated event has been applied: the host clears its own waiting state. */
    onQuestionsGenerated?: (kind: 'screening' | 'human') => void
    /** The host's request loader (`showHideBackdrop` in InterviewTab); fetches toggle it like every other request. */
    setRequestLoading?: (loading: boolean) => void
}

/**
 * The candidate's interview as the Interview tab holds it: fetched once per job application, refetched on the
 * server events and Marlee actions that change it, and shared with every flow through `setInterview`.
 *
 * `interview` is the `/api/interviews` payload (untyped, as upstream). `assessmentTemplate` is kept apart because
 * some responses carry a fresher copy of it than the interview itself. `emailRef` remembers the candidate's email so
 * responses that drop it can be merged back with it (`{ ...data, email: interview?.email || emailRef.current }`).
 */
export function useInterviewData({
    jobApplicationId,
    onInterviewDataChange,
    getCandidateList,
    onQuestionsGenerated,
    setRequestLoading,
}: UseInterviewDataOptions) {
    const { showErrorToast, showSuccessToast } = useToast()
    const [interview, setInterview] = useState<any>()
    const [assessmentTemplate, setAssessmentTemplate] = useState<any>()
    const [isLoading, setIsLoading] = useState(true)
    const emailRef = useRef<string | null>(null)
    const hasInterviewRef = useRef(false)
    hasInterviewRef.current = Boolean(interview)

    const getCandidateListRef = useRef(getCandidateList)
    useEffect(() => {
        getCandidateListRef.current = getCandidateList
    }, [getCandidateList])
    const refreshJobApplicationList = useCallback(() => {
        void getCandidateListRef.current?.()
    }, [])

    const onQuestionsGeneratedRef = useRef(onQuestionsGenerated)
    onQuestionsGeneratedRef.current = onQuestionsGenerated
    const setRequestLoadingRef = useRef(setRequestLoading)
    setRequestLoadingRef.current = setRequestLoading

    useEffect(() => {
        if (!interview?.email && emailRef.current) {
            setInterview({ ...interview, email: emailRef.current })
        }
    }, [interview])

    useEffect(() => {
        if (interview) {
            onInterviewDataChange?.(interview)
        }
    }, [interview, onInterviewDataChange])

    const getInterviewData = useCallback(
        async (showLoader = false) => {
            if (!jobApplicationId) return null

            if (showLoader || !hasInterviewRef.current) {
                setIsLoading(true)
            }

            return requestHandler(
                async () => await axios.get(`/api/interviews?jobApplicationId=${jobApplicationId}`),
                (data) => {
                    setInterview(data.data)
                    emailRef.current = data.data?.email
                    setAssessmentTemplate(data.data?.assessmentTemplate || undefined)
                    setIsLoading(false)
                },
                (errorMessage) => {
                    showErrorToast(errorMessage)
                    setIsLoading(false)
                },
                (loading) => setRequestLoadingRef.current?.(loading)
            )
        },
        [jobApplicationId, showErrorToast]
    )

    /** Refetch for an event about this job application; other applications' events are ignored. */
    const onOwnEvent = useCallback(
        (label: string, after: () => void) => (event: MessageEvent) => {
            try {
                const data = JSON.parse(event.data)
                console.log(`[SSE] ${label}:`, data)
                if (data.jobApplicationId === jobApplicationId) {
                    getInterviewData().then(after)
                }
            } catch (error) {
                console.error(`[SSE] Error parsing ${label} event:`, error)
            }
        },
        [jobApplicationId, getInterviewData]
    )

    useSSEV2({
        [InternalEventType.SCREENING_INTERVIEW_QUESTIONS_GENERATED]: useCallback(
            (event: MessageEvent) =>
                onOwnEvent('Screening interview questions generated', () => {
                    onQuestionsGeneratedRef.current?.('screening')
                    showSuccessToast('AI interview questions generated successfully')
                    refreshJobApplicationList()
                })(event),
            [onOwnEvent, showSuccessToast, refreshJobApplicationList]
        ),
        [InternalEventType.SCREENING_INTERVIEW_UPDATED]: useCallback(
            (event: MessageEvent) => onOwnEvent('Screening interview updated', refreshJobApplicationList)(event),
            [onOwnEvent, refreshJobApplicationList]
        ),
        [InternalEventType.HUMAN_INTERVIEW_QUESTIONS_GENERATED]: useCallback(
            (event: MessageEvent) =>
                onOwnEvent('Human interview questions generated', () => {
                    onQuestionsGeneratedRef.current?.('human')
                    showSuccessToast('AI interview questions generated successfully for panelist')
                })(event),
            [onOwnEvent, showSuccessToast]
        ),
        [InternalEventType.HUMAN_INTERVIEW_UPDATED]: useCallback(
            (event: MessageEvent) => onOwnEvent('Human interview updated', refreshJobApplicationList)(event),
            [onOwnEvent, refreshJobApplicationList]
        ),
    })

    useMarleeUIAction(
        'REFRESH_INTERVIEWS',
        useCallback(
            (payload) => {
                if (Number(payload.jobApplicationId) === jobApplicationId) {
                    getInterviewData().then(refreshJobApplicationList)
                }
            },
            [jobApplicationId, refreshJobApplicationList, getInterviewData]
        )
    )

    // TODO: use tanstack query to fetch interview data
    useEffect(() => {
        if (!jobApplicationId) return
        getInterviewData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobApplicationId])

    return {
        interview,
        setInterview: setInterview as Dispatch<SetStateAction<any>>,
        assessmentTemplate,
        setAssessmentTemplate: setAssessmentTemplate as Dispatch<SetStateAction<any>>,
        emailRef,
        isLoading,
        getInterviewData,
        refreshJobApplicationList,
    }
}
